import { ASK, GUIDES, SHELL, STARS } from './hubCopy'
import type { HubLang } from './i18n'

/**
 * THE PAGES ONE MAY POINT AT, BY THEIR OWN NAMES (HLP-37, 1 Nov 2026).
 *
 * A platform guide entry names the page it is about (`platform_help_entry.
 * route`, 346); under an answer the drawer shows one button per distinct page,
 * labelled with the name the page already carries in the navigation - never a
 * new phrasing, so the button and the nav item cannot drift apart. Two pages
 * are not nav items and use the label their own screen shows: the Store (Star
 * One's tab) and Guides (Help's).
 *
 * The routes are EXACTLY supabase/functions/ask-one/pages.ts's list (the one
 * the server and the database's CHECK use); the web suite asserts the two key
 * sets are equal and that each route is a <Route path> in App.tsx.
 */
export const PAGE_LABELS: Record<string, Record<HubLang, string>> = {
  '/': { en: SHELL.en.nav['Engagement Hub'], es: SHELL.es.nav['Engagement Hub'] },
  '/wiki': { en: SHELL.en.nav['Wiki One'], es: SHELL.es.nav['Wiki One'] },
  '/stars': { en: SHELL.en.nav['Star One'], es: SHELL.es.nav['Star One'] },
  '/stars/store': { en: STARS.en.store, es: STARS.es.store },
  '/contests': { en: SHELL.en.nav['Photo contests'], es: SHELL.es.nav['Photo contests'] },
  '/photos/gallery': { en: SHELL.en.nav.Gallery, es: SHELL.es.nav.Gallery },
  '/surveys': { en: SHELL.en.nav.Surveys, es: SHELL.es.nav.Surveys },
  '/profile': { en: SHELL.en.nav['Your profile'], es: SHELL.es.nav['Your profile'] },
  '/terms': { en: SHELL.en.nav.Terms, es: SHELL.es.nav.Terms },
  '/help': { en: SHELL.en.nav.Help, es: SHELL.es.nav.Help },
  '/help/guides': { en: GUIDES.en.title, es: GUIDES.es.title },
  '/support': { en: SHELL.en.nav['Support inbox'], es: SHELL.es.nav['Support inbox'] },
  '/admin': { en: SHELL.en.nav['Admin home'], es: SHELL.es.nav['Admin home'] },
  '/admin/people': { en: SHELL.en.nav['People and roles'], es: SHELL.es.nav['People and roles'] },
  // Suggestions is a tab of People and roles; the page is People and roles.
  '/admin/suggestions': { en: SHELL.en.nav['People and roles'], es: SHELL.es.nav['People and roles'] },
  '/admin/notices': { en: SHELL.en.nav.Notices, es: SHELL.es.nav.Notices },
  '/admin/usage': { en: SHELL.en.nav.Usage, es: SHELL.es.nav.Usage },
  '/admin/audit': { en: SHELL.en.nav['Audit log'], es: SHELL.es.nav['Audit log'] },
}

/** A page's name, or null for a route that is not one of these. */
export function pageLabel(route: string, lang: HubLang): string | null {
  return Object.prototype.hasOwnProperty.call(PAGE_LABELS, route) ? PAGE_LABELS[route][lang] : null
}

/**
 * ACTION PAGES (SUP-2, 1 Nov). A page opened in the state where a task is
 * DONE - the ticket form, the feedback form. Exactly supabase/functions/
 * ask-one/pages.ts's ACTION_PAGES (the suite holds the key sets equal and 347's
 * CHECK to the same list). Each is a known page plus the one query its screen
 * already parses (helpLink.ts, rule 20). Their buttons say the act, not the
 * page: "Open a ticket", not "Open Help".
 */
export const ACTION_LABELS: Record<string, Record<HubLang, string>> = {
  '/help?new=ticket': { en: ASK.en.openTicket, es: ASK.es.openTicket },
  '/help?new=feedback': { en: ASK.en.shareFeedback, es: ASK.es.shareFeedback },
}

/** A page One may link: a page above, or one of its action pages. */
export function isLinkPage(v: unknown): v is string {
  return (
    typeof v === 'string' &&
    (Object.prototype.hasOwnProperty.call(PAGE_LABELS, v) || Object.prototype.hasOwnProperty.call(ACTION_LABELS, v))
  )
}

/** The button for a linkable page: the act for an action page, "Open <page>"
 *  for a page; null for anything else. */
export function linkPageLabel(route: string, lang: HubLang): string | null {
  if (Object.prototype.hasOwnProperty.call(ACTION_LABELS, route)) return ACTION_LABELS[route][lang]
  const page = pageLabel(route, lang)
  return page ? ASK[lang].openPage(page) : null
}
